import { Box } from '@mui/material';
import { Users, UserPlus, UserMinus, Contact, LogIn, Briefcase } from 'lucide-react';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { StatCard } from '@/components/cards/StatCard';
import { SectionHeader } from '@/components/dashboard/SectionHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { QuickActionCard } from '@/components/dashboard/QuickActionCard';
import { PageHeader } from '@/components/navigation/PageHeader';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useTeachers } from '@/hooks/useTeachers';
import { useAuth } from '@/hooks/useAuth';
import { ROLES } from '@/constants/roles';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '@/router/paths';

export function TeacherDashboardPage() {
  usePageMeta('Teacher Management', [
    { label: 'Teachers', path: PATHS.TEACHERS },
    { label: 'Overview' },
  ]);

  const { teachers, loading } = useTeachers();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Basic derived stats
  const totalTeachers = teachers.length;
  const activeTeachers = teachers.filter(t => t.status === 'active').length;
  const inactiveTeachers = totalTeachers - activeTeachers;
  
  // Calculate unique departments
  const departments = new Set(teachers.map(t => t.department).filter(Boolean));
  const totalDepartments = departments.size;

  const canManage = user?.role === ROLES.ORGANIZATION_ADMIN || user?.role === ROLES.PRINCIPAL;

  return (
    <Box>
      <PageHeader
        title="Teacher Overview"
        subtitle="High-level metrics and actions for teacher management."
      />

      <SectionHeader title="Staffing & Metrics" />
      <DashboardGrid columns={{ xs: 1, sm: 2, md: 4 }}>
        <StatCard title="Total Teachers" value={loading ? '...' : totalTeachers} icon={Users} color="primary" onClick={() => navigate(PATHS.TEACHERS_LIST)} />
        <StatCard title="Active Teachers" value={loading ? '...' : activeTeachers} icon={Users} color="success" onClick={() => navigate(PATHS.TEACHERS_LIST, { state: { status: 'active' } })} />
        <StatCard title="Inactive" value={loading ? '...' : inactiveTeachers} icon={UserMinus} color="error" onClick={() => navigate(PATHS.TEACHERS_LIST, { state: { status: 'inactive' } })} />
        <StatCard title="Departments" value={loading ? '...' : totalDepartments} icon={Briefcase} color="secondary" onClick={() => navigate(PATHS.TEACHERS_LIST)} />
      </DashboardGrid>

      {canManage && (
        <WidgetContainer title="Quick Actions" sx={{ mt: 3 }}>
          <Box sx={{ p: 1 }}>
            <DashboardGrid columns={{ xs: 1, sm: 2, md: 4 }} gap={1}>
              <QuickActionCard title="View All Teachers" icon={Users} path={PATHS.TEACHERS_LIST} color="primary" />
              <QuickActionCard title="Add New Teacher" icon={UserPlus} path={PATHS.TEACHERS_ADD} color="success" />
              <QuickActionCard title="Teacher Directory" icon={Contact} path={PATHS.TEACHER_DIRECTORY} color="info" />
              <QuickActionCard title="Import Teachers" icon={LogIn} path={PATHS.TEACHERS_LIST} color="secondary" />
            </DashboardGrid>
          </Box>
        </WidgetContainer>
      )}
    </Box>
  );
}
